/*
 * 50-50 Large Promo sticky content — scoped per [data-large-promo-root] root.
 * Non-hero: CSS centers the content initially (lg:items-center); JS pins to viewport bottom mid-scroll.
 * Hero ([data-hero-placement="true"]): JS pins to viewport bottom from the moment the section enters view.
 * Hooks: [data-large-promo-root], [data-large-promo-sticky-layer],
 *        [data-large-promo-sticky-slot], [data-large-promo-sticky-content].
 */

// Must stay in sync with Tailwind's lg: breakpoint
const style = getComputedStyle(document.documentElement);
const DESKTOP_BREAKPOINT = parseInt(style.getPropertyValue('--breakpoint-lg'), 10) || 1024;

class LargePromoStickyContent {
  constructor(root) {
    this.root = root;
    this.isHero = root.dataset.heroPlacement === 'true';
    this.entries = [];
    root.querySelectorAll('[data-large-promo-sticky-content]').forEach((content) => {
      const layer = content.closest('[data-large-promo-sticky-layer]');
      const slot = content.closest('[data-large-promo-sticky-slot]');
      if (layer && slot) this.entries.push({ content, layer, slot });
    });
    this.desktopQuery = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const offset = parseFloat(getComputedStyle(root).getPropertyValue('--spacing-2xl'));
    this.bottomOffset = Number.isFinite(offset) && offset > 0 ? offset : 32;
    this.frame = null;

    if (!this.entries.length) return;

    this.update = this.update.bind(this);
    this.queueUpdate = this.queueUpdate.bind(this);
    window.addEventListener('scroll', this.queueUpdate, { passive: true });
    window.addEventListener('resize', this.queueUpdate, { passive: true });
    this.desktopQuery.addEventListener?.('change', this.queueUpdate);
    this.update();
  }

  destroy() {
    window.removeEventListener('scroll', this.queueUpdate);
    window.removeEventListener('resize', this.queueUpdate);
    this.desktopQuery.removeEventListener?.('change', this.queueUpdate);
    if (this.frame) cancelAnimationFrame(this.frame);
    this.entries.forEach(({ content }) => this.resetContent(content));
  }

  queueUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(this.update);
  }

  update() {
    this.frame = null;

    this.entries.forEach(({ content, layer, slot }) => {
      if (!this.desktopQuery.matches) {
        this.resetContent(content);
        return;
      }

      const layerRect = layer.getBoundingClientRect();
      const slotRect = slot.getBoundingClientRect();
      const contentHeight = content.getBoundingClientRect().height;
      const contentWidth = slotRect.width;

      if (!layerRect.height || !contentHeight || contentHeight >= layerRect.height) {
        this.resetContent(content);
        return;
      }

      // Three reference positions, all measured from the layer's top:
      //  - centerTop:        where CSS centers the content on initial load.
      //  - viewportBottomTop: where a viewport-bottom-anchored content would sit.
      //  - maxTop:           the last layer-relative position before the content
      //                      would clip past the layer's bottom edge.
      const centerTop = (layerRect.height - contentHeight) / 2;
      const fixedTop = window.innerHeight - this.bottomOffset - contentHeight;
      const viewportBottomTop = fixedTop - layerRect.top;
      const maxTop = layerRect.height - this.bottomOffset - contentHeight;

      // Non-hero only: above the centered position, let CSS handle it.
      if (!this.isHero && viewportBottomTop <= centerTop) {
        this.resetContent(content);
        return;
      }

      // Past the layer end: anchor absolutely to the layer's bottom.
      if (viewportBottomTop >= maxTop) {
        this.applyPosition(content, 'absolute', maxTop, slotRect.left - layerRect.left, contentWidth);
        return;
      }

      // Mid-scroll: pin to the viewport bottom.
      this.applyPosition(content, 'fixed', fixedTop, slotRect.left, contentWidth);
    });
  }

  applyPosition(content, position, top, left, width) {
    content.style.position = position;
    content.style.top = `${top}px`;
    content.style.left = `${left}px`;
    content.style.right = 'auto';
    content.style.width = `${width}px`;
  }

  resetContent(content) {
    content.style.position = '';
    content.style.top = '';
    content.style.left = '';
    content.style.right = '';
    content.style.width = '';
  }
}

function initSections(container = document) {
  const selector = '[data-large-promo-root]:not([data-promos-sticky-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);
  roots.forEach((root) => {
    root.dataset.promosStickyInit = 'true';
    root._stickyInstance = new LargePromoStickyContent(root);
  });
}

initSections();
document.addEventListener('shopify:section:load', (event) => initSections(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector?.('[data-large-promo-root]') || event.target;
  if (root?._stickyInstance) {
    root._stickyInstance.destroy();
    delete root._stickyInstance;
  }
});
